/**
 * Bringing an EXISTING Jasper report in — a .zip bundle of a main report with
 * its subreports, or loose .jrxml files — instead of generating one.
 *
 * Two surfaces offer it and must agree on what an import means: the Report
 * designer's header (files land in the open designer, the user saves), and the
 * workbench object node's Details tab (no designer mounted — the model is
 * written straight to project storage). So the reading, the main-report
 * detection, the model patch and the criteria rows live here once.
 *
 * The FILES are read by the sibling designer's own `readReportBundle`
 * (@jasper/embed), so zip expansion, __MACOSX filtering and the subreport
 * reference graph that decides which file is main are the SAME rules the
 * designer's own toolbar import uses — imported through a dynamic import so
 * the jasper chunk only downloads when somebody actually imports.
 */
import type { JasperEmbedFile, JrxmlParameter } from '@jasper/embed'
import type { CriteriaRule } from '../visual/types'
import type { ReportModel } from './types'

function javaClassToColtype(javaClass: string): 'string' | 'number' | 'date' {
  const c = javaClass || ''
  if (/Integer|Long|Short|Double|Float|BigDecimal|BigInteger|Number/.test(c)) return 'number'
  if (/Date|Timestamp|Time/.test(c)) return 'date'
  return 'string'
}

// A JRXML default that means "today", in the spellings designs use.
const TODAY_EXPR = /^(new\s+(java\.util\.)?Date\(\s*\)|java\.sql\.Date\.valueOf\(\s*java\.time\.LocalDate\.now\(\s*\)\s*\))$/

/**
 * A parameter's <defaultValueExpression> as a criteria default.
 *
 * The expression is JAVA; a criteria value is a literal or a platform token,
 * and it travels on to the legacy XSD's `value` attribute. Only a plain literal
 * (already unquoted by the parser) or a recognisable "today" carries over —
 * any other expression becomes an empty prompt rather than Java source shown
 * in the prompt box.
 */
function criteriaDefault(raw: string | undefined, coltype: string): string {
  const v = (raw || '').trim()
  if (!v) return ''
  if (coltype === 'date') {
    if (TODAY_EXPR.test(v)) return ':calendar_today'
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : ''
  }
  if (coltype === 'number') return /^-?\d+(\.\d+)?$/.test(v) ? v : ''
  return /[()$"]/.test(v) ? '' : v
}

function titleize(name: string): string {
  return name.replace(/[_-]+/g, ' ').replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
}

/**
 * Ensure one criteria rule per JRXML $P{} parameter (keyed by JASPER_PARAM).
 *
 * A row the JRXML has just gained takes its prompt heading from the
 * parameter's own <parameterDescription> when it declares one, so a parameter
 * arriving from the Design tab, from a hand-edited design or from a re-import
 * carries the heading it was authored with. Only a parameter with no
 * description falls back to a title-cased name — and saving never adds one
 * (report_sidecar_sync writes headings to the XSD, and only keeps a
 * description a parameter already has in step), so that fallback is where a
 * plain <parameter …/> stays.
 */
export function syncRulesWithParams(rules: CriteriaRule[], params: JrxmlParameter[]): CriteriaRule[] {
  const byJasper = new Map<string, CriteriaRule>()
  for (const r of rules) {
    const jp = String(r.JASPER_PARAM || r.field || '').trim()
    if (jp) byJasper.set(jp, r)
  }
  const next: CriteriaRule[] = []
  for (const p of params) {
    const existing = byJasper.get(p.name)
    if (existing) {
      next.push(existing)
      byJasper.delete(p.name)
    } else {
      next.push({
        id: `Criteria_${p.name}`,
        field: p.name,
        COLTYPE: javaClassToColtype(p.javaClass),
        currentFieldType: javaClassToColtype(p.javaClass),
        operator: '=',
        queryOption: 'P',
        prevOption: 'P',
        promptLabel: (p.description || '').trim() || titleize(p.name),
        value: criteriaDefault(p.defaultValue, javaClassToColtype(p.javaClass)),
        chipValue: [],
        JASPER_PARAM: p.name,
        lookup: '', lookup_sql: '', lookup_title: '', lookup_sql_data_col: '',
        options: [],
      } as CriteriaRule)
    }
  }
  // Orphans (rules whose $P{} no longer exists) stay LAST, flagged in the UI.
  next.push(...byJasper.values())
  return next
}

export interface ReadReportFiles {
  files: JasperEmbedFile[]
  /** The file the bundle's own subreport references make the main report. */
  main: JasperEmbedFile
  /** {filename: content} — the save/load transport shape. */
  jrxmlFiles: Record<string, string>
  /** The main report's declared $P{} parameters. */
  params: JrxmlParameter[]
}

/**
 * Read the user's picked files. Throws with the reader's own wording when the
 * selection holds no report source at all.
 */
export async function readPickedReportFiles(picked: File[]): Promise<ReadReportFiles> {
  const { readReportBundle, parseJrxmlParameters } = await import('@jasper/embed')
  const files = await readReportBundle(picked)
  const main = files.find((f) => f.isMain) ?? files[0]
  if (!main) throw new Error('No .jrxml or .xml files found')
  let params: JrxmlParameter[] = []
  try {
    params = parseJrxmlParameters(main.content)
  } catch {
    // Malformed XML — the designer surfaces it on open; the files still import.
  }
  return {
    files,
    main,
    jrxmlFiles: Object.fromEntries(files.map((f) => [f.filename, f.content])),
    params,
  }
}

/**
 * Point a model at an imported file set: which file is main, the file list,
 * the save-time parameter cache and one criteria row per $P{} parameter.
 * Returns a NEW model; every key the designer knows nothing about (an imported
 * report's REPORT.LEGACY_SOURCE, say) rides through untouched.
 */
export function applyImportedFiles(model: ReportModel, read: ReadReportFiles): ReportModel {
  const next = JSON.parse(JSON.stringify(model)) as ReportModel
  next.REPORT.MAIN_JRXML = read.main.filename
  next.REPORT.JRXML_FILES = read.files.map((f) => f.filename)
  next.REPORT.PARAMETERS = read.params.map((p) => ({ name: p.name, java_class: p.javaClass }))
  next.SQLMODEL.CRITERIA.query.rules =
    syncRulesWithParams(next.SQLMODEL.CRITERIA.query.rules, read.params)
  return next
}
